import type { FC } from 'react';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { MarketplaceLeadStatus } from '@gcv/shared';
import { Box, CircularProgress, Menu, MenuItem } from '@mui/material';

import { useComponent } from 'hooks';
import { useUpdateBankMarketplaceLead } from 'hooks/mutations';
import { useBank, useBankMarketplaceLeads } from 'hooks/queries';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { Button, Divider, Drawer, InputPhone, InputSelect, LabelValue, Spinner, Text } from 'ui';
import { theme } from 'ui/theme';
import { DateTimeHelpers } from 'util/dateTime.util';
import { userInterestsReadableNameMap } from 'util/org.util';
import { ArchiveLeadModal } from './archive-lead-modal';
import { ConvertLeadModal } from './convert-lead-modal';

export const LeadDrawer: FC<{
  leadId: string;
  isOpen: boolean;
  onClose: () => void;
}> = useComponent((props) => {
  const { leadId, isOpen, onClose } = props;

  const navigate = useNavigate();
  const { bank } = useBank();
  const { bankMarketplaceLeads } = useBankMarketplaceLeads();
  const { updateBankMarketplaceLead, isPutBankMarketplaceLeadPending } = useUpdateBankMarketplaceLead();

  const [convertMenuEl, setConvertMenuEl] = useState<Element>();
  const [archiveLeadModalOpen, setArchiveLeadModalOpen] = useState<boolean>(false);
  const [convertLeadModalOpen, setConvertLeadModalOpen] = useState<boolean>(false);
  const [archiveLeadStatus, setArchiveLeadStatus] = useState<MarketplaceLeadStatus>();

  const lead = bankMarketplaceLeads?.find((l) => l.id === leadId);

  const form = useForm({
    mode: 'onBlur',
    defaultValues: lead
      ? ({
          status: lead.status
        } as FieldValues)
      : {}
  });

  if (!lead || !bank) {
    return <Spinner />;
  }

  const updateLead = async (props: {
    status: MarketplaceLeadStatus;
    internalComment?: string;
    externalComment?: string;
  }) => {
    try {
      await updateBankMarketplaceLead({
        id: lead.id,
        ...props
      });
    } catch {
      getSnackbarStore().showErrorSnackbarMessage(
        'There was an issue updating the lead. Contact support for additional help.'
      );
    }
  };

  return (
    <>
      <Drawer
        id="lead-drawer"
        title={lead.company_name}
        open={isOpen}
        onClose={onClose}
        bottomAction={
          ![
            MarketplaceLeadStatus.Invited,
            MarketplaceLeadStatus.Not_A_Fit,
            MarketplaceLeadStatus.Unqualified
          ].includes(lead.status) && (
            <Box sx={{ display: 'flex', gap: 1, padding: 2, justifyContent: 'center' }}>
              <Button
                label={'Send Application Request'}
                onClick={() => setConvertLeadModalOpen(true)}
                color={'primary'}
              />
              {/* <Button
              label={'Convert Lead to Account'}
              onClick={(event) => setConvertMenuEl(event.currentTarget)}
              color={'primary'}
              endIcon="keyboard_arrow_down_rounded"
            /> */}
            </Box>
          )
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 2 }}>
            {lead.status === MarketplaceLeadStatus.Invited ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Text content="This lead has been invited to your program" />
                <Button
                  label={'Go to Account'}
                  onClick={() => navigate(`/secure/fi/accounts/${lead.crb_id}/view`)}
                  color="primary"
                />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <Box sx={{ maxWidth: 325, flexGrow: 1 }}>
                    <InputSelect
                      label={'Lead Status'}
                      name={'status'}
                      options={Object.values(MarketplaceLeadStatus)
                        .filter((s) => s !== MarketplaceLeadStatus.Invited)
                        .map((s) => {
                          return { value: s, label: s };
                        })}
                      additionalOnChange={(e) => {
                        const status = e.target.value as MarketplaceLeadStatus;
                        if (
                          status === MarketplaceLeadStatus.Not_A_Fit ||
                          status === MarketplaceLeadStatus.Unqualified
                        ) {
                          setArchiveLeadStatus(status);
                          setArchiveLeadModalOpen(true);
                        } else {
                          updateBankMarketplaceLead({
                            id: lead.id,
                            status: status
                          });
                        }
                      }}
                      {...form}
                    />
                  </Box>
                  {isPutBankMarketplaceLeadPending && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: theme.palette.primary.main,
                        margin: 'auto'
                      }}
                    />
                  )}
                </Box>
                {lead.status === MarketplaceLeadStatus.Not_A_Fit ||
                lead.status === MarketplaceLeadStatus.Unqualified ? (
                  <LabelValue label={'Internal Comment'} value={lead.fi_internal_comment ?? '--'} />
                ) : (
                  <Text content="Track your progress working this lead" type="body3" />
                )}
              </>
            )}

            <Divider />
          </Box>
          <LabelValue
            label={'Date Created'}
            value={DateTimeHelpers.formatISOToFriendlyDateString(lead.date_created, bank.iana_timezone)}
          />
          <LabelValue
            label={'Last Updated'}
            value={DateTimeHelpers.formatISOToDateAtTimeString(lead.date_updated, bank.iana_timezone)}
          />
          <LabelValue label={'Contact Name'} value={lead.user_name} />
          <LabelValue label={'Contact Email'} value={lead.user_email} />
          <InputPhone
            label={'Contact Phone'}
            name="phone"
            defaultValue={lead.user_phone}
            viewOnly
            {...form}
          />
          <LabelValue label={'Company'} value={lead.company_name} />
          <LabelValue
            label={'Company Interests'}
            value={(lead.user_interests ?? []).map((i) => userInterestsReadableNameMap[i]).join(', ')}
          />
          <LabelValue label={'Location'} value={`${lead.company_city} ${lead.company_state}`.trim()} />
          <LabelValue label={'Comment'} value={lead.crb_comment ?? '--'} />
          <LabelValue label={'Offer'} value={lead.marketplace_offer.title} />
        </Box>
      </Drawer>

      <ArchiveLeadModal
        lead={lead}
        status={archiveLeadStatus}
        isOpen={archiveLeadModalOpen}
        onClose={(newStatus) => {
          if (newStatus) {
            form.setValue('status', newStatus);
          } else {
            form.resetField('status');
          }
          setArchiveLeadModalOpen(false);
        }}
      />

      <ConvertLeadModal
        lead={lead}
        isOpen={convertLeadModalOpen}
        onClose={() => {
          setConvertLeadModalOpen(false);
        }}
      />

      <Menu
        id="convert-menu"
        anchorEl={convertMenuEl}
        open={Boolean(convertMenuEl)}
        onClose={() => setConvertMenuEl(undefined)}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{}}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setConvertMenuEl(undefined);
            console.log('TODO send application request');
          }}
          sx={{ minWidth: '249px' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Text content={'Send Application Request'} />
            <Text content={'Lead completes application'} type="body3" />
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setConvertMenuEl(undefined);
            console.log('TODO convert t oactive');
          }}
          sx={{ minWidth: '249px' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Text content={'Convert Directly to Active'} />
            <Text content={'Skip application Process'} type="body3" />
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
});
